<template>
  <div class="network-tree-node" :style="{ '--depth': depth }">
    <div
      class="network-tree-node__card"
      :class="{
        'network-tree-node__card--premium': node.isPremiumActive,
        'network-tree-node__card--non-premium': !node.isPremiumActive,
      }"
    >
      <div class="network-tree-node__header">
        <v-btn v-if="hasChildren" icon x-small @click="toggleExpanded">
          <v-icon small>{{ expanded ? "mdi-chevron-down" : "mdi-chevron-right" }}</v-icon>
        </v-btn>
        <span v-else class="network-tree-node__spacer" />
        <v-chip x-small outlined class="level-chip mr-2">N{{ node.level }}</v-chip>
        <span class="network-tree-node__name">{{ node.name }}</span>
        <v-chip
          x-small
          :color="node.isPremiumActive ? '#2dcc6b' : '#9e9e9e'"
          dark
          class="ml-2"
        >
          {{ node.isPremiumActive ? "Premium" : "Não Premium" }}
        </v-chip>
        <v-chip
          v-if="!node.pagbankConnected"
          x-small
          color="#FFF3E0"
          text-color="#E65100"
          class="ml-1"
        >
          Sem PagBank
        </v-chip>
      </div>
      <div class="network-tree-node__meta">
        <span><v-icon x-small>mdi-identifier</v-icon> {{ shortId(node.id) }}</span>
        <span><v-icon x-small>mdi-card-account-details</v-icon> {{ formatDocument(node.document) }}</span>
        <span v-if="node.email"><v-icon x-small>mdi-email</v-icon> {{ node.email }}</span>
        <span v-if="node.phone"><v-icon x-small>mdi-phone</v-icon> {{ formatPhone(node.phone) }}</span>
        <span v-if="node.premiumJoinedAt">
          <v-icon x-small>mdi-star</v-icon> Premium: {{ formatDate(node.premiumJoinedAt) }}
        </span>
        <span class="network-tree-node__earned">
          <v-icon x-small>mdi-cash</v-icon> Gerado: {{ formatCurrency(node.generatedForSponsor) }}
        </span>
      </div>
    </div>
    <div v-if="expanded && hasChildren" class="network-tree-node__children">
      <network-tree-node
        v-for="child in node.children"
        :key="child.id"
        :node="child"
        :depth="depth + 1"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { format } from "date-fns";
import Vue from "vue";
import { PremiumNetworkNode } from "@/types/premium/PremiumNetwork";

export default Vue.extend({
  name: "NetworkTreeNode",
  props: {
    node: {
      type: Object as () => PremiumNetworkNode,
      required: true,
    },
    depth: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      expanded: this.depth <= 2,
    };
  },
  computed: {
    hasChildren(): boolean {
      return (this.node.children?.length ?? 0) > 0;
    },
  },
  methods: {
    toggleExpanded() {
      this.expanded = !this.expanded;
    },
    formatCurrency(value: number) {
      return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    formatDate(value: string) {
      try {
        return format(new Date(value), "dd/MM/yyyy");
      } catch {
        return "—";
      }
    },
    formatDocument(value: string) {
      if (!value) return "—";
      const digits = value.replace(/\D/g, "");
      if (digits.length === 11) {
        return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      }
      return value;
    },
    formatPhone(value: string) {
      const digits = String(value || "").replace(/\D/g, "");
      if (digits.length < 10) return value || "—";
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    },
    shortId(id: string) {
      return id ? `${id.slice(0, 8)}…` : "—";
    },
  },
});
</script>

<style scoped lang="scss">
.network-tree-node__card {
  border-left: 4px solid #ccc;
  padding: 12px 14px;
  margin: 6px 0;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
}
.network-tree-node__card--premium {
  border-left-color: #2dcc6b;
  background: linear-gradient(90deg, rgba(45, 204, 107, 0.04), #fff 40%);
}
.network-tree-node__card--non-premium {
  border-left-color: #bdbdbd;
}
.network-tree-node__header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  font-weight: 600;
}
.network-tree-node__name {
  font-size: 0.95rem;
}
.network-tree-node__spacer {
  width: 28px;
  display: inline-block;
}
.level-chip {
  font-weight: 700;
  min-width: 32px;
  justify-content: center;
}
.network-tree-node__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 16px;
  font-size: 0.78rem;
  color: #555;
  margin-top: 8px;
  padding-left: 32px;
}
.network-tree-node__earned {
  font-weight: 600;
  color: #1b7d42;
}
.network-tree-node__children {
  margin-left: 20px;
  border-left: 2px dashed #e0e0e0;
  padding-left: 10px;
}
</style>
